import { Renderer } from 'amis';
import {RendererProps} from 'amis';
import React, {useState, useEffect, useRef, useCallback} from 'react';
import {Empty, Spin, Button, message, Dropdown, Menu, Modal, Input} from 'antd';
import {VChart} from '@visactor/react-vchart';
import RGL, {WidthProvider} from 'react-grid-layout';
import {DashboardInfo, ChartConfig, SheetInfo} from './types';
import {ChartConfigModal} from './components/ChartConfigModal';
import { PermissionTag } from './components/PermissionTag';
import { processChartData } from './utils/chartDataProcessor';
import {
  MoreOutlined,
  EditOutlined,
  CopyOutlined,
  DeleteOutlined,
  FormOutlined,
  DragOutlined,
  PlusCircleOutlined,
  MenuUnfoldOutlined
} from '@ant-design/icons';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './style.css';
import {AIChartService} from './services/AIChartService';
import dashboardService from './services/dashboardService';
import sheetService from './services/sheetService';
(window as any).AIChartService = AIChartService;

const ReactGridLayout = WidthProvider(RGL);

interface DashboardViewProps extends RendererProps {
  loading: boolean;
  height: string | number;
  dashboard: DashboardInfo | null;
  parentDashboardId: number | null;
  parentAllSheetList: SheetInfo[];
  siderCollapsed: boolean;
  isShowPermissionTag?: boolean;
  onToggleSider: () => void;
}


export const DashboardView: React.FC<DashboardViewProps> = ({
  loading,
  height,
  dashboard,
  parentDashboardId,
  parentAllSheetList,
  siderCollapsed = false,
  isShowPermissionTag = false,
  onToggleSider,
}) => {
  const [allSheetList, setAllSheetList] = useState<SheetInfo[]>([]);
  const [dashboardId, setDashboardId] = useState<number | null>(null);
  const [chartModalVisible, setChartModalVisible] = useState(false);
  const [charts, setCharts] = useState<ChartConfig[]>([]);
  const [isFetchingCharts, setFetchingCharts] = useState(false);
  const saveTimeoutRef = useRef<any>(null);
  const [focusedChartId, setFocusedChartId] = useState<number | null>(null);
  const [isRenameModalVisible, setIsRenameModalVisible] = useState(false);
  const [renamingChart, setRenamingChart] = useState<ChartConfig | null>(null);
  const [newChartName, setNewChartName] = useState('');
  const [editingChart, setEditingChart] = useState<ChartConfig | null>(null);

  // 预览模式下禁用交互
  const MODE = (window as any).AGIMX_CONFIG.MODE;
  const hasPermission = MODE === 'editor';

  // 初始获取仪表盘图表
  useEffect(() => {
    fetchDashboardData();
  }, []);

  // 监听 parentDashboardId 变化以获取新的仪表盘数据
  useEffect(() => {
    if (parentDashboardId) {
      console.log("parentDashboardId changed, fetching data:", parentDashboardId);
      setDashboardId(parentDashboardId);
      fetchChartsForDashboard(parentDashboardId);
    }
  }, [parentDashboardId]);

  // 初始获取所有集合
  useEffect(() => {
    if(parentAllSheetList && parentAllSheetList.length > 0){
      setAllSheetList(parentAllSheetList);
      return;
    }
    handleGetAllSheets();
  }, []);

  // 新增：监听批量添加图表的事件
  useEffect(() => {
    const handleAddMultipleCharts = async (event: CustomEvent) => {
      const newCharts: ChartConfig[] = event.detail;
      if (!dashboardId || !newCharts || newCharts.length === 0) {
        return;
      }

      message.loading({
        content: `正在添加 ${newCharts.length} 个图表...`,
        key: 'add-charts'
      });

      // 获取当前仪表盘中已有的图表数量，用于计算新图表的布局位置
      const existingChartCount = charts.length;
      const addedCharts: ChartConfig[] = [];

      for (let i = 0; i < newCharts.length; i++) {
        const newChartConfig = newCharts[i];

        // 使用 generateLayout 服务来获取正确的布局信息，并传入现有图表数量作为偏移量
        const layout = aiChartService.generateLayout(i, existingChartCount);

        const chartToSave: ChartConfig = {
          ...newChartConfig,
          code: `chart-${Date.now()}-${i}`,
          layout: {
            ...layout,
            i: `chart-${Date.now()}-${i}`
          }
        };

        try {
          const {data, ...chartSpecWithoutData} = chartToSave.chartSpec;
          const chartPayload = {
            parentId: dashboardId,
            data_source: chartToSave.dataSource,
            code: chartToSave.code,
            type: chartToSave.chartType,
            title: chartToSave.title,
            spec: JSON.stringify(chartSpecWithoutData),
            layout: JSON.stringify(chartToSave.layout)
          };

          const res = await dashboardService.createDashboardChart(chartPayload);
          chartToSave.id = res.id;
          addedCharts.push(chartToSave);
        } catch (error) {
          console.error('添加图表失败:', error);
          message.error(`添加图表 "${chartToSave.title}" 失败`);
        }
      }

      setCharts(prevCharts => [...prevCharts, ...addedCharts]);
      message.success({
        content: `成功添加 ${addedCharts.length} 个图表!`,
        key: 'add-charts',
        duration: 2
      });
    };

    // 在 DashboardView 内部实例化 AIChartService
    const aiChartService = new (window as any).AIChartService();
    // const aiChartService = new AIChartService();

    window.addEventListener(
      'add-ai-charts',
      handleAddMultipleCharts as EventListener
    );

    return () => {
      window.removeEventListener(
        'add-ai-charts',
        handleAddMultipleCharts as EventListener
      );
    };
  }, [dashboardId, charts]);

  const fetchDashboardData = async () => {
    // 获取前端url路由以'/'分割的最后一部分作为dashboardId
    
    const dashboardCode = window.location.hash.split('/').pop();
    
    if (!dashboardCode ) {
      setCharts([]);
      return;
    }
    
    try {
      if(parentDashboardId) return;
      //   setDashboardId(parentDashboardId);
      //   fetchChartsForDashboard(parentDashboardId);
      // }else{
        const dashboardList = await dashboardService.getDashboardList();
        const isExistDashboard = dashboardList.find((d: any) => d.code === dashboardCode);
        const dashboardId = isExistDashboard?.id;
        console.log('仪表盘存在性检查:', isExistDashboard, dashboardId);
        setDashboardId(dashboardId || null);
        if (dashboardId) {
          fetchChartsForDashboard(dashboardId);
        } else {
          console.log("仪表盘不存在，创建仪表盘...");
          
          setCharts([]);
          // 创建仪表盘
          const res = await dashboardService.createDashboard({
            name: `仪表盘${dashboardCode}`,
            code: dashboardCode,
          });
          setDashboardId(res.id);
        }
    //   }
    } catch (error) {
      console.error('检查仪表盘存在性失败:', error);
      message.error('检查仪表盘失败');
      setCharts([]);
    }
  };

  const fetchChartsForDashboard = async (dashboardId: number) => {
    const fetchCharts = async () => {
      setFetchingCharts(true);
      try {
        const savedCharts = await dashboardService.getDashboardCharts(dashboardId);

        // 收集所有唯一的数据源
        const uniqueDataSources = Array.from(
          new Set(savedCharts.map((chart: any) => chart.data_source))
        );

        // 批量获取所有数据源的数据，建立缓存
        const dataSourceCache: Record<string, any[]> = {};
        // console.log('uniqueDataSources', uniqueDataSources);

        // 并发请求所有数据源
        await Promise.all(
          uniqueDataSources.map(async (dataSource: string) => {
            try {
              console.log('Fetching records for dataSource:', dataSource);
              const records = await sheetService.getTableData(dataSource, ['*']);
              // console.log(`仪表盘原始数据:`, records);

              // 为每条记录添加记录行数字段
              const processedRecords = records.map((record: any) => ({
                ...record,
                记录行数: 1
              }));

              dataSourceCache[dataSource] = processedRecords;
            } catch (error) {
              console.error(`获取数据源 ${dataSource} 失败:`, error);
              dataSourceCache[dataSource] = [];
            }
          })
        );

        // 使用缓存的数据构建图表配置
        let formattedCharts: ChartConfig[] = [];
        for (let chart of savedCharts) {
          const records = dataSourceCache[chart.data_source] || [];

          let chartSpec;
          try {
            chartSpec = JSON.parse(chart.spec);
          } catch (error) {
            console.error('解析图表spec失败:', error);
            continue;
          }

          // 使用新的数据处理函数
          const options = {
            records,
            chartSpec,
          }
          const processedData = processChartData(options);

          const formattedChart = {
            id: chart.id,
            code: chart.code,
            title: chart.title,
            dataSource: chart.data_source,
            chartType: chart.type,
            chartSpec: {
              ...chartSpec,
              data: {
                values: processedData
              }
            },
            layout: JSON.parse(chart.layout)
          };
          formattedCharts.push(formattedChart);
        }

        console.log('Formatted Charts:', formattedCharts);
        setCharts(formattedCharts || []);
      } catch (error) {
        console.error('获取图表失败:', error);
        message.error('获取图表失败');
        setCharts([]);
      } finally {
        setFetchingCharts(false);
      }
    };
    fetchCharts();
  };

  const handleGetAllSheets = async () => {
    try {
      const originalSheets: SheetInfo[] = await sheetService.getAllSheets();
      console.log('所有工作表:', originalSheets);
      const newSheets = originalSheets.map(sheet => ({
        ...sheet,
        name: sheet.meta?.translations?.[0].translation || sheet.collection,
      }));
      console.log('所有工作表1:', newSheets);
      
      setAllSheetList(newSheets);
    } catch (error) {
      console.error('获取所有工作表失败:', error);
      message.error('获取所有工作表失败');
    }
  };

  const handleAddChart = async (newChartConfig: ChartConfig) => {
    console.log('Adding new chart:', newChartConfig,dashboardId);
    
    if (!dashboardId) {
      message.error('无法添加图表，缺少仪表盘或API');
      return;
    }
    try {
      console.log('Saving new charts:', newChartConfig);

      // 传递给接口的spec字段取newChartConfig.chartSpec，删掉里面的data字段
      const {data, ...chartSpecWithoutData} = newChartConfig.chartSpec;
      const chartPayload = {
        parentId: dashboardId,
        data_source: newChartConfig.dataSource,
        code: newChartConfig.code,
        type: newChartConfig.chartType,
        title: newChartConfig.title,
        spec: JSON.stringify(chartSpecWithoutData),
        layout: JSON.stringify(newChartConfig.layout)
      };

      const res = await dashboardService.createDashboardChart(chartPayload);

      // 返回的res.id重新赋值给newChartConfig.id
      newChartConfig.id = res.id;
      newChartConfig.title = newChartConfig.title || `图表 ${res.id}`;

      // 更新本地状态
      setCharts([...charts, newChartConfig]);
      message.success('图表已添加');
    } catch (error) {
      message.error(
        `添加图表失败:${error?.errors?.[0]?.message || error.message}`
      );
    }
  };

  // 添加更新图表的处理函数
  const handleUpdateChart = async (updatedChartConfig: ChartConfig) => {
    if (!editingChart || !dashboardId) {
      message.error('无法更新图表');
      return;
    }

    try {
      const {data, ...chartSpecWithoutData} = updatedChartConfig.chartSpec;
      const chartPayload = {
        data_source: updatedChartConfig.dataSource,
        type: updatedChartConfig.chartType,
        title: updatedChartConfig.title,
        spec: JSON.stringify(chartSpecWithoutData),
        layout: JSON.stringify(editingChart.layout) // 保持原有布局
      };

      await dashboardService.updateDashboardChart(editingChart.id, chartPayload);

      // 更新本地状态
      setCharts(
        charts.map(chart =>
          chart.id === editingChart.id
            ? {
                ...updatedChartConfig,
                id: editingChart.id,
                layout: editingChart.layout
              }
            : chart
        )
      );

      message.success('图表已更新');
      setEditingChart(null);
    } catch (error) {
      message.error(`更新图表失败: ${error?.message || '未知错误'}`);
    }
  };

  // 使用useCallback创建防抖的保存函数
  const debouncedSaveLayout = useCallback(
    (layout: RGL.Layout[]) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(async () => {
        if (!dashboardId) return;

        try {
          for (const layoutItem of layout) {
            const chart = charts.find(c => c.code === layoutItem.i);
            if (chart?.id) {
              const layoutPayload = {
                layout: JSON.stringify(layoutItem)
              };
              await dashboardService.updateDashboardChart(chart.id, layoutPayload);
            }
          }
          console.log('布局已保存');
        } catch (error) {
          console.error('保存布局失败:', error);
          message.error('保存布局失败');
        }
      }, 1000);
    },
    [dashboardId, charts]
  );

  const onLayoutChange = (layout: RGL.Layout[]) => {
    const updatedCharts = charts.map(chart => {
      const layoutItem = layout.find(l => l.i === chart.code);
      return layoutItem ? {...chart, layout: layoutItem} : chart;
    });
    setCharts(updatedCharts);
    debouncedSaveLayout(layout);
  };

  // 清理定时器
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // 处理图表点击聚焦
  const handleChartClick = (chartId: number, event: React.MouseEvent) => {
    console.log('Chart clicked:', chartId, focusedChartId);
    event.stopPropagation();
    setFocusedChartId(chartId);
  };

  // 点击其他地方取消聚焦
  const handleContainerClick = () => {
    setFocusedChartId(null);
  };

  // 图表操作菜单
  const getChartMenu = (chart: ChartConfig) => (
    <Menu className="dashboard-chart-menu">
      <Menu.Item
        key="edit"
        icon={<EditOutlined />}
        onClick={() => handleEditChart(chart)}
        className="dashboard-chart-menu-item"
      >
        编辑
      </Menu.Item>
      <Menu.Item
        key="rename"
        icon={<FormOutlined />}
        onClick={() => handleRenameChart(chart)}
        className="dashboard-chart-menu-item"
      >
        重命名
      </Menu.Item>
      <Menu.Item
        key="duplicate"
        icon={<CopyOutlined />}
        onClick={() => handleDuplicateChart(chart)}
        className="dashboard-chart-menu-item"
      >
        创建副本
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item
        key="delete"
        icon={<DeleteOutlined />}
        danger
        onClick={() => handleDeleteChart(chart)}
        className="dashboard-chart-menu-item dashboard-chart-menu-item-danger"
      >
        删除
      </Menu.Item>
    </Menu>
  );

  // 编辑图表
const handleEditChart = (chart: ChartConfig) => {
  setEditingChart(chart);
  setChartModalVisible(true);
};

  // 重命名图表
  const handleRenameChart = (chart: ChartConfig) => {
    setRenamingChart(chart);
    setNewChartName(chart.title || `图表 ${chart.id}`);
    setIsRenameModalVisible(true);
  };

  // 确认重命名
  const handleRenameConfirm = async () => {
    if (!renamingChart || !newChartName.trim()) {
      message.warning('请输入图表名称');
      return;
    }

    try {
      await dashboardService.updateDashboardChart(renamingChart.id, {
        title: newChartName.trim()
      });

      setCharts(
        charts.map(chart =>
          chart.id === renamingChart.id
            ? {...chart, title: newChartName.trim()}
            : chart
        )
      );

      message.success('重命名成功');
      setIsRenameModalVisible(false);
      setRenamingChart(null);
      setNewChartName('');
    } catch (error) {
      message.error('重命名失败');
      console.error('重命名失败:', error);
    }
  };

  // 创建副本
  const handleDuplicateChart = async (chart: ChartConfig) => {
    try {
      const duplicatedChart = {
        ...chart,
        id: 0, // 临时设置为0，稍后会被服务器返回的id替换
        code: `chart-${Date.now()}`,
        title: `${chart.title || '图表'} 副本`,
        layout: {
          ...chart.layout,
          i: `chart-${Date.now()}`,
          x: (chart.layout.x + chart.layout.w) % 12,
          y:
            chart.layout.y +
            (chart.layout.x + chart.layout.w >= 12 ? chart.layout.h : 0)
        }
      };

      const {data, ...chartSpecWithoutData} = duplicatedChart.chartSpec;
      const chartPayload = {
        parentId: dashboardId,
        data_source: duplicatedChart.dataSource,
        code: duplicatedChart.code,
        type: duplicatedChart.chartType,
        title: duplicatedChart.title,
        spec: JSON.stringify(chartSpecWithoutData),
        layout: JSON.stringify(duplicatedChart.layout)
      };

      const res = await dashboardService.createDashboardChart(chartPayload);
      duplicatedChart.id = res.id;

      setCharts([...charts, duplicatedChart]);
      message.success('副本创建成功');
    } catch (error) {
      message.error('创建副本失败');
      console.error('创建副本失败:', error);
    }
  };

  // 删除图表
  const handleDeleteChart = (chart: ChartConfig) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除图表 "${
        chart.title || chart.id
      }" 吗？此操作无法撤销。`,
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await dashboardService.deleteDashboardChart(chart.id);
          setCharts(charts.filter(c => c.id !== chart.id));

          if (focusedChartId === chart.id) {
            setFocusedChartId(null);
          }

          message.success('图表已删除');
        } catch (error) {
          message.error('删除失败');
          console.error('删除失败:', error);
        }
      }
    });
  };

  if (loading || isFetchingCharts) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%'
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // if (!dashboardId) {
  //   return (
  //     <div
  //       className="dashboard-empty-state"
  //       style={{
  //         height: `calc(${height} - 50px)`
  //       }}
  //     >
  //       <Empty description={false} />
  //       <h2>请创建一个仪表盘</h2>
  //       <p>创建一个仪表盘来查看和分析数据</p>
  //     </div>
  //   );
  // }
  const allPermission = hasPermission || (isShowPermissionTag && dashboard?.permission === 'ALL');
  return (
    <div
      style={{
        // height: `calc(${height} - 50px)`,
        height: height,
        overflow: 'auto'
      }}
      className="dashboard-grid-container"
      onClick={handleContainerClick}
    >
      <div style={{display: 'flex', justifyContent: 'space-between', padding: '0 10px'}}>
        <div>
          {siderCollapsed && (
            // <MenuUnfoldOutlined className="header-icon" onClick={onToggleSider} />
            <button className='glass-button' onClick={onToggleSider}><MenuUnfoldOutlined /></button>
          )}
          {charts.length > 0 && allPermission && <button 
            onClick={() => setChartModalVisible(true)} 
            className='glass-button'
          >
            <PlusCircleOutlined />添加图表
          </button>}
        </div>  
        {isShowPermissionTag && <PermissionTag dashboard={dashboard} />}
      </div>
      {charts.length === 0 ? (
        <div className="dashboard-empty-state">
          <Empty
            image="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
            description={false}
          />
          <h2>用仪表盘实时展示业务趋势</h2>
          <p>
            可添加柱状图、饼图、折线图等图表展示数据，关注业务动向，快速获取洞察
          </p>
          <Button className='dashboard-add-chart-button' onClick={() => setChartModalVisible(true)}>
            添加图表
          </Button>
        </div>
      ) : (
        <>
          <ReactGridLayout
            className="layout"
            layout={charts.map(c => c.layout)}
            onLayoutChange={onLayoutChange}
            cols={12}
            rowHeight={30}
            margin={[16, 16]}
            isDraggable={allPermission}
            isResizable={allPermission}
            draggableHandle=".drag-handle-icon"
          >
            {charts.map(chart => {
              // const hasPermission = MODE === 'view';
              return (
                <div
                  key={chart.code}
                  className={`dashboard-card ${
                    focusedChartId === chart.id ? 'focused' : ''
                  } ${allPermission ? 'user-can-drag' : ''}`}
                  onClick={e => {
                    // 检查用户是否有权限，如果没有权限则不触发点击事件
                    if (allPermission) {
                      handleChartClick(chart.id, e);
                    }
                  }}
                >
                  {/* 头部工具栏 */}
                  <div
                    className="dashboard-card-header"
                    onClick={e => e.stopPropagation()}
                  >
                    {/* 图表标题 */}
                    <div
                      className="dashboard-card-title"
                      style={{
                        flex: 1,
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {chart.title || `图表 ${chart.id}`}
                    </div>

                    {/* 中间拖拽区域 */}
                    {allPermission && <div
                      className="drag-handle-icon"
                      onMouseDown={e => {
                        if (!allPermission) {
                          e.preventDefault();
                          return;
                        }
                      }}
                      title="拖拽图表"
                    >
                      <DragOutlined style={{fontSize: '14px', color: '#555'}} />
                    </div>}

                    {/* 操作按钮 */}
                    <div
                      style={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'flex-end'
                      }}
                    >
                      {focusedChartId === chart.id && (
                        <div style={{display: 'flex', alignItems: 'center'}}>
                          <Dropdown
                            overlay={getChartMenu(chart)}
                            trigger={['click']}
                            placement="bottomRight"
                          >
                            <Button
                              type="text"
                              size="small"
                              icon={<MoreOutlined />}
                              className="dashboard-action-button"
                            />
                          </Dropdown>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 图表内容区域 */}
                  <div className="dashboard-card-content">
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        // pointerEvents: 'none',
                        userSelect: 'none'
                      }}
                    >
                      <VChart
                        key={`chart-${chart.code}`}
                        spec={{
                          ...chart.chartSpec
                        }}
                        // options={{
                        //   autoFit: true,
                        //   animation: false
                        // }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </ReactGridLayout>
        </>
      )}

      {/* 重命名模态框 */}
      <Modal
        title="重命名图表"
        open={isRenameModalVisible}
        onOk={handleRenameConfirm}
        onCancel={() => {
          setIsRenameModalVisible(false);
          setRenamingChart(null);
          setNewChartName('');
        }}
        okText="确认"
        cancelText="取消"
        className="dashboard-rename-modal"
      >
        <Input
          value={newChartName}
          onChange={e => setNewChartName(e.target.value)}
          placeholder="请输入图表名称"
          onPressEnter={handleRenameConfirm}
        />
      </Modal>

      <ChartConfigModal
        visible={chartModalVisible}
        onClose={() => {
          setChartModalVisible(false);
          setEditingChart(null);
        }}
        onAddChart={editingChart ? handleUpdateChart : handleAddChart}
        allSheetList={allSheetList}
        editingChart={editingChart}
      />
    </div>
  );
};

@Renderer({
  test: /\bagimx-dashboard$/,
  name: 'agimx-dashboard'
})

export default class DashboardPlugin extends React.Component<DashboardViewProps> {
  render() {
    return <DashboardView {...this.props} />;
  }
}